import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Stethoscope, Building2, Droplets, Shirt, Calendar, Briefcase } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FACT_CONFIG = [
  { key: 'surgery_time', label: 'Expected Treatment Time', Icon: Clock },
  { key: 'anaesthesia', label: 'Anaesthesia', Icon: Stethoscope },
  { key: 'hospitalisation', label: 'Hospitalisation', Icon: Building2 },
  { key: 'drains_required', label: 'Drains Required', Icon: Droplets },
  { key: 'supporting_garment', label: 'Supporting Garment', Icon: Shirt },
  { key: 'follow_up', label: 'Follow-up', Icon: Calendar }
];

function QuickFactsGrid({ facts }) {
  if (!facts) return null;
  const items = FACT_CONFIG.filter((item) => {
    const val = facts[item.key];
    return val && val !== 'Not applicable';
  });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((item) => {
        const IconComp = item.Icon;
        return (
          <div key={item.key} className="p-6 bg-clinic-bg border border-clinic-border" data-testid={'quick-fact-' + item.key}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center border border-clinic-border flex-shrink-0">
                <IconComp size={18} className="text-clinic-charcoal" />
              </div>
              <div>
                <p className="text-caption mb-1">{item.label}</p>
                <p className="font-body text-clinic-charcoal">{facts[item.key]}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ProcedureDetailPage() {
  const params = useParams();
  const [procedure, setProcedure] = useState(null);
  const [treatment, setTreatment] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProcedure = useCallback(() => {
    axios.get(API + '/treatments/' + params.treatmentId)
      .then((res) => {
        setTreatment(res.data.treatment);
        if (res.data.treatment.detailed_procedures) {
          const proc = res.data.treatment.detailed_procedures.find(
            (p) => p.id === params.procedureId
          );
          setProcedure(proc);
        }
        setLoading(false);
      })
      .catch(() => { setLoading(false); });
  }, [params.treatmentId, params.procedureId]);

  useEffect(() => {
    fetchProcedure();
  }, [fetchProcedure]);

  if (loading) {
    return (
      <div className="bg-clinic-bg min-h-screen pt-32">
        <div className="container-clinic">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-clinic-muted w-32" />
            <div className="h-16 bg-clinic-muted w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!procedure || !treatment) {
    return (
      <div className="bg-clinic-bg min-h-screen pt-32" data-testid="procedure-not-found">
        <div className="container-clinic text-center">
          <h1 className="text-heading-2 text-clinic-charcoal mb-6">Procedure not found</h1>
          <Link to="/treatments" className="btn-secondary">View All Treatments</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-clinic-bg" data-testid="procedure-detail-page">
      <div className="pt-28 md:pt-32">
        <div className="container-clinic">
          <Link 
            to={'/treatments/' + params.treatmentId} 
            className="inline-flex items-center gap-2 text-sm font-body text-clinic-stone hover:text-clinic-charcoal transition-colors"
            data-testid="back-to-treatment-link"
          >
            <ArrowLeft size={16} />
            Back to {treatment.title}
          </Link>
        </div>
      </div>

      <section className="pt-8 pb-16 md:pb-24" data-testid="procedure-hero">
        <div className="container-clinic">
          <div className="max-w-3xl">
            <p className="text-caption mb-4">{treatment.title}</p>
            <h1 className="text-heading-1 text-clinic-charcoal mb-8">{procedure.name}</h1>
            <p className="text-body-lg mb-6">{procedure.description}</p>
            <p className="text-body-lg">{procedure.detailed_info}</p>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white" data-testid="quick-facts-section">
        <div className="container-clinic">
          <div className="mb-12">
            <p className="text-caption mb-4">At a Glance</p>
            <h2 className="text-heading-2 text-clinic-charcoal">Procedure Details</h2>
          </div>
          <QuickFactsGrid facts={procedure.quick_facts} />
        </div>
      </section>

      <section className="section-padding" data-testid="recovery-section">
        <div className="container-clinic">
          <div className="mb-12">
            <p className="text-caption mb-4">Recovery</p>
            <h2 className="text-heading-2 text-clinic-charcoal">Return to Activities</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 bg-white border border-clinic-border" data-testid="recovery-light">
              <div className="w-12 h-12 flex items-center justify-center border border-clinic-border mb-6">
                <Briefcase size={20} className="text-clinic-charcoal" />
              </div>
              <h3 className="text-heading-3 text-clinic-charcoal mb-2">Light Work</h3>
              <p className="text-xl font-heading text-clinic-stone mb-3">{procedure.recovery.light_work}</p>
              <p className="text-body-lg text-sm">Office work, light daily activities</p>
            </div>
            <div className="p-8 bg-white border border-clinic-border" data-testid="recovery-moderate">
              <div className="w-12 h-12 flex items-center justify-center border border-clinic-border mb-6">
                <Briefcase size={20} className="text-clinic-charcoal" />
              </div>
              <h3 className="text-heading-3 text-clinic-charcoal mb-2">Moderate Physical Work</h3>
              <p className="text-xl font-heading text-clinic-stone mb-3">{procedure.recovery.moderate_work}</p>
              <p className="text-body-lg text-sm">More active roles, light exercise</p>
            </div>
            <div className="p-8 bg-white border border-clinic-border" data-testid="recovery-heavy">
              <div className="w-12 h-12 flex items-center justify-center border border-clinic-border mb-6">
                <Briefcase size={20} className="text-clinic-charcoal" />
              </div>
              <h3 className="text-heading-3 text-clinic-charcoal mb-2">Heavy Physical Work</h3>
              <p className="text-xl font-heading text-clinic-stone mb-3">{procedure.recovery.heavy_work}</p>
              <p className="text-body-lg text-sm">Full workouts, strenuous activities</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-clinic-muted" data-testid="procedure-cta-section">
        <div className="container-clinic">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-heading-2 text-clinic-charcoal mb-6">
              Interested in {procedure.name}?
            </h2>
            <p className="text-body-lg mb-10">
              Book a consultation to discuss your goals and determine if this procedure is right for you.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="procedure-cta-btn">
              Request a Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ProcedureDetailPage;
